// Per spec §7.19: vendor_timezone Day 1 propagation. Source of truth for every
// card's date display (Tasks 20-22 depend on this).
//
// Contract:
// - Visitor in PST viewing a venue in EST sees the date in EST.
// - ISO with explicit offset (Z or +HH:MM/-HH:MM): render in vendor_timezone
//   (NOT offset's TZ, NOT browser-local).
// - ISO without offset (naive): interpret AS vendor_timezone, render in vendor_timezone.
// - Unparseable input: return verbatim (graceful degradation; cards display the
//   LLM's raw string).
// - Invalid IANA vendor_timezone: fall back to UTC + console.warn.
// - Empty/null/undefined: return "" / never throw.
//
// Known v1 limitation per spec §9 (DST edge case): the naive-ISO offset adjustment
// uses the offset at the parsed-as-UTC instant, NOT the offset at the wall-clock
// instant in vendor_timezone. Around DST transitions (2 days/year) this can render
// off by 1 hour for naive inputs. Acceptable v1 behavior — schema descriptions
// explicitly request ISO WITH offset, cards have unparseable-date fallback, and
// date-fns-tz (~10KB gz) was rejected as a v1 cost given the LLM is told to avoid
// the failure mode. v1.5 may add date-fns-tz if real traffic shows this is common.

const ISO_OFFSET_RE = /([+-]\d{2}:?\d{2}|Z)$/
// ISO 8601 shape: YYYY-MM-DD optionally followed by Thh:mm[:ss[.sss]] and offset.
// Anything not matching this is "unparseable" and returned verbatim — this is
// stricter than `new Date(...)` parsing, which leniently accepts strings like
// "Saturday Oct 19" and produces the wrong date.
const ISO_SHAPE_RE = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2}(\.\d+)?)?([+-]\d{2}:?\d{2}|Z)?)?$/

const FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
}

export function formatDatetime(
  iso: string | null | undefined,
  vendor_timezone: string,
): string {
  if (iso === null || iso === undefined || iso === '') return ''

  try {
    // Strict ISO shape check before delegating to Date — `new Date(...)` is
    // lenient and would parse "Saturday Oct 19" as a valid date.
    if (!ISO_SHAPE_RE.test(iso)) return iso

    const hasOffset = ISO_OFFSET_RE.test(iso)

    let date: Date
    if (hasOffset) {
      date = new Date(iso)
      if (isNaN(date.getTime())) return iso // unparseable -> verbatim
    } else {
      // Naive ISO: treat the wall-clock as if it's in vendor_timezone. Strategy:
      // parse as UTC, then shift by vendor_timezone's offset at that moment so
      // the formatted output's wall-clock matches the input's wall-clock.
      const naive = new Date(iso + 'Z')
      if (isNaN(naive.getTime())) return iso // unparseable -> verbatim

      let offsetMin = 0
      try {
        const tzDtf = new Intl.DateTimeFormat('en-US', {
          timeZone: vendor_timezone,
          timeZoneName: 'longOffset',
        })
        const parts = tzDtf.formatToParts(naive)
        const tzPart = parts.find((p) => p.type === 'timeZoneName')?.value || 'GMT+0'
        const m = tzPart.match(/GMT([+-])(\d{2}):?(\d{2})?/)
        if (m) {
          const sign = m[1] === '-' ? -1 : 1
          const hours = parseInt(m[2], 10)
          const mins = parseInt(m[3] || '0', 10)
          offsetMin = sign * (hours * 60 + mins)
        }
      } catch {
        // vendor_timezone invalid; offsetMin stays 0 (UTC interpretation).
        // The validation block below will warn + fall back to UTC for rendering.
      }
      date = new Date(naive.getTime() - offsetMin * 60 * 1000)
    }

    // Validate vendor_timezone; fall back to UTC + warn on invalid.
    let tz = vendor_timezone
    try {
      new Intl.DateTimeFormat('en-US', { timeZone: tz })
    } catch {
      console.warn(
        '[format-datetime] invalid timezone "' +
          String(vendor_timezone) +
          '", falling back to UTC',
      )
      tz = 'UTC'
    }

    return new Intl.DateTimeFormat('en-US', { ...FORMAT_OPTIONS, timeZone: tz }).format(date)
  } catch {
    // Ultimate fallback: never throw out of this function.
    return iso
  }
}
